import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'

interface LegalPageContainerProps
	extends React.HTMLAttributes<HTMLDivElement> {}

const LegalPageContainer = React.forwardRef<
	HTMLDivElement,
	LegalPageContainerProps
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn('container mx-auto max-w-3xl space-y-6 px-4 py-8', className)}
		{...props}
	/>
))
LegalPageContainer.displayName = 'LegalPageContainer'

interface LegalPageTitleProps
	extends React.HTMLAttributes<HTMLHeadingElement> {}

const LegalPageTitle = React.forwardRef<
	HTMLHeadingElement,
	LegalPageTitleProps
>(({ className, ...props }, ref) => (
	<h1
		ref={ref}
		className={cn('text-3xl font-bold tracking-tight sm:text-4xl', className)}
		{...props}
	/>
))
LegalPageTitle.displayName = 'LegalPageTitle'

interface LegalPageEffectiveDateProps
	extends React.HTMLAttributes<HTMLParagraphElement> {}

const LegalPageEffectiveDate = React.forwardRef<
	HTMLParagraphElement,
	LegalPageEffectiveDateProps
>(({ className, ...props }, ref) => (
	<p
		ref={ref}
		className={cn('text-sm italic text-muted-foreground', className)}
		{...props}
	/>
))
LegalPageEffectiveDate.displayName = 'LegalPageEffectiveDate'

interface LegalPageSectionTitleProps
	extends React.HTMLAttributes<HTMLHeadingElement> {}

const LegalPageSectionTitle = React.forwardRef<
	HTMLHeadingElement,
	LegalPageSectionTitleProps
>(({ className, ...props }, ref) => (
	<h2
		ref={ref}
		className={cn(
			'mb-3 mt-8 border-b pb-2 text-2xl font-semibold tracking-tight first:mt-0',
			className,
		)}
		{...props}
	/>
))
LegalPageSectionTitle.displayName = 'LegalPageSectionTitle'

interface LegalPageParagraphProps
	extends React.HTMLAttributes<HTMLParagraphElement> {}

const LegalPageParagraph = React.forwardRef<
	HTMLParagraphElement,
	LegalPageParagraphProps
>(({ className, ...props }, ref) => (
	<p ref={ref} className={cn('leading-7', className)} {...props} />
))
LegalPageParagraph.displayName = 'LegalPageParagraph'

interface LegalPageListProps extends React.HTMLAttributes<HTMLUListElement> {}

const LegalPageList = React.forwardRef<HTMLUListElement, LegalPageListProps>(
	({ className, ...props }, ref) => (
		<ul
			ref={ref}
			className={cn('my-4 ml-6 list-disc space-y-2', className)}
			{...props}
		/>
	),
)
LegalPageList.displayName = 'LegalPageList'

interface LegalPageListItemProps extends React.HTMLAttributes<HTMLLIElement> {}

const LegalPageListItem = React.forwardRef<
	HTMLLIElement,
	LegalPageListItemProps
>(({ className, ...props }, ref) => (
	<li ref={ref} className={cn('', className)} {...props} />
))
LegalPageListItem.displayName = 'LegalPageListItem'

interface LegalPageLinkProps
	extends React.AnchorHTMLAttributes<HTMLAnchorElement> {}

const LegalPageLink = React.forwardRef<HTMLAnchorElement, LegalPageLinkProps>(
	({ className, ...props }, ref) => (
		<a
			ref={ref}
			className={cn(
				'font-medium text-primary underline underline-offset-4 hover:text-primary/90',
				className,
			)}
			{...props}
		/>
	),
)
LegalPageLink.displayName = 'LegalPageLink'

export {
	LegalPageContainer,
	LegalPageEffectiveDate,
	LegalPageLink,
	LegalPageList,
	LegalPageListItem,
	LegalPageParagraph,
	LegalPageSectionTitle,
	LegalPageTitle,
}
